import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { BRAND_ASSETS, TENANT } from '../config';

const TermsOfService: React.FC = () => {
  const isRedexBuild = process.env.REACT_APP_TENANT_ID === 'redex';
  const platformName = TENANT.branding.name;
  const navigate = useNavigate();
  // Opened in a new tab (e.g. from the sign-up consent links) there's no history to go
  // back to — fall back to home so the user is never stranded on the page.
  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/');
  };

  if (isRedexBuild) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <div className="bg-white border-b border-neutral-200">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between h-16">
              <Link to="/" className="flex items-center space-x-2 text-neutral-600 hover:text-neutral-900">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span>Back to Home</span>
              </Link>
              <img src={BRAND_ASSETS.logo.primary} alt={platformName} className="h-8 w-auto" />
            </div>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-xl shadow-card border border-neutral-200 p-8 md:p-12"
          >
            <h1 className="text-4xl font-bold text-neutral-900 mb-4">Terms of Service</h1>
            <p className="text-sm text-neutral-500 mb-8">Last Updated: February 17, 2026</p>

            <div className="prose prose-neutral max-w-none">
              <p className="text-neutral-700 mb-6">
                These Terms of Service (these "Terms") govern your access to and use of the {platformName} website, portals,
                dashboards, APIs, and related services (collectively, the "Services"). By accessing or using the Services,
                you agree to these Terms.
              </p>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-neutral-900 mb-4">1. Eligibility and Account Security</h2>
                <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                  <li>You must be at least 18 years old to use the Services.</li>
                  <li>You are responsible for maintaining the confidentiality of your credentials and any connected wallets.</li>
                  <li>You agree to provide accurate information and keep it up to date.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-neutral-900 mb-4">2. No Advice</h2>
                <p className="text-neutral-700 mb-4">
                  The Services may include tools, data, or workflows related to transactions. The Services do not provide legal,
                  tax, or investment advice. You are responsible for your decisions and for consulting your own advisers.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-neutral-900 mb-4">3. Risk Disclosure</h2>
                <p className="text-neutral-700 mb-4">
                  Platform usage involves risk, including operational risk, transaction errors, and potential loss of funds.
                  Blockchain transactions may be irreversible. Always verify destinations, approvals, and amounts.
                </p>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-neutral-900 mb-4">4. Acceptable Use</h2>
                <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                  <li>Do not use the Services for unlawful activity.</li>
                  <li>Do not attempt to bypass security, rate limits, or access controls.</li>
                  <li>Do not interfere with or disrupt the Services.</li>
                </ul>
              </section>

              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-neutral-900 mb-4">5. Contact</h2>
                <p className="text-neutral-700">
                  Questions about these Terms can be sent to{' '}
                  <a href={`mailto:${TENANT.branding.supportEmail}`} className="text-primary hover:underline">
                    {TENANT.branding.supportEmail}
                  </a>.
                </p>
              </section>
            </div>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-background border-b border-border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <button
              onClick={handleBack}
              className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back</span>
            </button>
            <img src={BRAND_ASSETS.logo.primary} alt={platformName} className="h-8 w-auto" />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div
          className="bg-background rounded-xl shadow-card border border-border p-8 md:p-12"
        >
          <h1 className="text-4xl font-bold text-foreground mb-4">Terms of Service</h1>
          <p className="text-sm text-muted-foreground mb-8">Last Updated: December 8, 2025</p>

          <div className="prose prose-neutral max-w-none font-sans">
            <p className="text-neutral-700 mb-6">
              These Terms of Service (these "Terms") set out the legally binding terms and conditions that govern your use of the services provided by Honeyshares LLC and its affiliates (collectively, "Honeyshares," "we," "us," or "our"), including our website, currently located at honeyshares.com, and any related mobile application, portals, dashboards, content, tools, and other products or services we make available (collectively, the "Services").
            </p>
            <p className="text-neutral-700 mb-6">
              The Services are intended for use by residents of the United States in connection with offerings that are permitted under applicable law in the jurisdictions where users reside and where the offerings are made.
            </p>
            <p className="text-foreground font-semibold mb-6">PLEASE READ THESE TERMS CAREFULLY.</p>
            <div className="bg-[#F0F0F0] border-2 border-[#242424] rounded-lg p-4 mb-8">
              <p className="text-[#242424] font-semibold">
                THESE TERMS CONTAIN A BINDING ARBITRATION AGREEMENT AND A WAIVER OF CLASS ACTION AND JURY TRIAL.
              </p>
            </div>

            {/* Section 1 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">1. Acceptance of Terms; Eligibility; Privacy</h2>
              <p className="text-neutral-700 mb-4">
                By accessing or using the Services in any way (including visiting the Site, creating an account, submitting information, or viewing investment information), you represent and agree that:
              </p>
              <ol className="list-decimal pl-6 mb-4 text-neutral-700 space-y-3">
                <li>You are at least 18 years of age and have the legal capacity and authority to enter into these Terms, either on your own behalf or on behalf of an entity you are authorized to bind.</li>
                <li>You have read, understand, and agree to be bound by these Terms, as they may be updated from time to time.</li>
                <li>You have read and understand our <Link to="/privacy" className="text-[#1944FF] font-medium hover:underline">Privacy Policy</Link>, which is incorporated into these Terms by reference, and you consent to our collection, use, and sharing of information as described there.</li>
                <li>If these Terms have changed since the last time you accessed the Services, your continued use of the Services constitutes your acceptance of the updated Terms.</li>
                <li>You consent to receive communications from us electronically (including via email, in-app messaging, SMS, and postings on the Services) and agree that such communications satisfy any legal requirements that such communications be in writing.</li>
              </ol>
              <p className="text-neutral-700 font-medium">
                If you do not agree to these Terms, you must not access or use the Services.
              </p>
            </section>

            {/* Section 2 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">2. No Investment, Legal, or Tax Advice</h2>
              <p className="text-neutral-700 mb-4">
                Nothing on or through the Services is intended to be, and you agree not to construe it as:
              </p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>Investment, financial, tax, legal, or accounting advice;</li>
                <li>A solicitation or offer to buy or sell any securities, investment products, or financial instruments;</li>
                <li>An endorsement of any particular investment or investment strategy; or</li>
                <li>A personalized recommendation for you or any entity.</li>
              </ul>
              <p className="text-neutral-700 mb-4">
                Information presented through the Services (including any projections, performance data, or "Honeyshares Content" as defined below) is provided solely for informational and educational purposes. Any investment decisions you make are solely your responsibility.
              </p>

              <h3 className="text-xl font-semibold text-neutral-800 mb-3">General Risks</h3>
              <p className="text-neutral-700 mb-4">
                Investing involves risk, including the risk of loss of principal. Investments in real estate and in securities tied to real estate:
              </p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>Are generally illiquid and may have no active secondary market;</li>
                <li>May experience significant changes in value, including total loss;</li>
                <li>Are not FDIC-insured or guaranteed by any government agency;</li>
                <li>Are subject to market, economic, regulatory, and other risks;</li>
                <li>May have returns affected by fees, expenses, and conflicts of interest; and</li>
                <li>May not perform as projected or expected.</li>
              </ul>
              <p className="text-neutral-700 mb-4">
                Past performance is not a guarantee of future results. Any projections or forward-looking statements are hypothetical, are based on assumptions that may change, and cannot be guaranteed.
              </p>

              <h3 className="text-xl font-semibold text-neutral-800 mb-3">Your Representations Regarding Investment Decisions</h3>
              <p className="text-neutral-700 mb-4">
                By using the Services in connection with any investment opportunity, you represent, warrant, and agree that:
              </p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>You are solely responsible for your own investment research and decision-making;</li>
                <li>You have made and will make your own independent evaluation of any investment opportunity;</li>
                <li>You have (or have consulted with) advisers with the knowledge and experience to evaluate any investment, or you have waived that right knowingly;</li>
                <li>You will review and understand all applicable offering documents before investing;</li>
                <li>You meet the investor eligibility requirements for any offering in which you participate;</li>
                <li>You can bear the economic risk of any investment, including total loss;</li>
                <li>Honeyshares is not your fiduciary and owes you no duty to give advice; and</li>
                <li>You release Honeyshares from any liability related to your investment decisions.</li>
              </ul>
              <p className="text-neutral-700">
                Any investment you make will be governed solely by the applicable offering documents, which you should review carefully.
              </p>
            </section>

            {/* Section 3 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">3. License and Use of the Services</h2>
              <p className="text-neutral-700 mb-4">
                Subject to your continued compliance with these Terms, Honeyshares grants you a limited, non-exclusive, non-transferable, non-sublicensable, revocable license to access and use the Services for your personal, non-commercial use (or, if you are an institutional investor, for your internal investment evaluation purposes).
              </p>
              <p className="text-neutral-700 mb-4">You are responsible for:</p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>Obtaining and maintaining all hardware, software, and network connectivity needed to access the Services;</li>
                <li>Providing accurate, current, and complete information as requested;</li>
                <li>Maintaining the confidentiality of your account and login credentials;</li>
                <li>All activities that occur under your account.</li>
              </ul>
              <p className="text-neutral-700">
                We may from time to time release updates, changes, or new features of the Services. Certain features may require the latest version of our software or app. We do not guarantee that the Services will function on any particular device or configuration.
              </p>
            </section>

            {/* Section 4 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">4. Availability and Changes to the Services</h2>
              <p className="text-neutral-700 mb-4">We reserve the right, at any time and without notice:</p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>To modify, suspend, or discontinue any part or all of the Services;</li>
                <li>To impose limits on certain features or restrict access to parts or all of the Services;</li>
                <li>To deny access to the Services to any user or account;</li>
                <li>To change, update, remove, or add content, features, or offerings;</li>
                <li>To vary or limit your access based on your location, investor status, regulatory requirements, or other factors.</li>
              </ul>
              <p className="text-neutral-700">
                We are not liable to you or any third party for any modification, suspension, or discontinuation of the Services or any part thereof.
              </p>
            </section>

            {/* Section 5 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">5. Account Registration and Security</h2>
              <p className="text-neutral-700 mb-4">
                Some portions or features of the Services may require you to register for an account.
              </p>
              <p className="text-neutral-700 mb-4">When creating or updating an account, you agree to:</p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>Provide accurate, current, and complete information (including identity verification documents as required);</li>
                <li>Update your information promptly if it changes;</li>
                <li>Comply with any additional terms or requirements applicable to your account type;</li>
                <li>Not create or maintain more than one account per person unless authorized;</li>
                <li>Not create an account using a false or misleading identity;</li>
                <li>Not share your account credentials or allow others to access your account;</li>
                <li>Not use another person's account without their permission and our authorization.</li>
              </ul>
              <p className="text-neutral-700 mb-4">
                You are responsible for all activities that occur under your account, whether or not authorized by you. You must keep your login credentials confidential and secure and notify us immediately if you suspect any unauthorized access to or use of your account or any other breach of security.
              </p>
              <p className="text-neutral-700">
                We may close, suspend, or restrict access to your account at any time, with or without notice, if we believe you have violated these Terms or applicable law, or for any other reason permitted by law.
              </p>
            </section>

            {/* Section 6 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">6. Prohibited Conduct</h2>
              <p className="text-neutral-700 mb-4">You agree that you will not, and will not assist, enable, or encourage others to:</p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>Use the Services for any illegal purpose or in violation of any applicable law or regulation;</li>
                <li>Use the Services to defraud, mislead, or deceive any person;</li>
                <li>Violate or infringe on the rights of others, including intellectual property, privacy, or publicity rights;</li>
                <li>Upload, transmit, or distribute any content that is defamatory, obscene, threatening, abusive, harassing, or otherwise objectionable;</li>
                <li>Attempt to bypass, defeat, or interfere with any security features or access restrictions;</li>
                <li>Interfere with the operation of the Services or any user's access to the Services;</li>
                <li>Harvest, scrape, or collect data from the Services without our express written permission;</li>
                <li>Use any automated means (including bots, scrapers, or crawlers) to access the Services;</li>
                <li>Reverse engineer, decompile, or disassemble any part of the Services;</li>
                <li>Impersonate another person or entity, or misrepresent your affiliation with any person or entity;</li>
                <li>Engage in any activity that could damage, disable, overburden, or impair the Services;</li>
                <li>Circumvent, disable, or interfere with any digital rights management or copy protection mechanisms.</li>
              </ul>
              <p className="text-neutral-700">
                Where applicable law would permit you to do something that is restricted above, you agree not to exercise that permission without giving us at least 30 days' prior written notice at <a href="mailto:support@honeyshares.com" className="text-[#1944FF] font-medium hover:underline">support@honeyshares.com</a>, so that we may assess and, at our discretion, attempt to offer alternative solutions.
              </p>
            </section>

            {/* Section 7 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">7. Intellectual Property; Honeyshares Content</h2>
              <p className="text-neutral-700 mb-4">
                All content and materials made available through the Services, including but not limited to text, graphics, logos, images, illustrations, video, audio, data, software, and the selection and arrangement thereof, together with any Honeyshares trademarks, trade names, logos, and branding (collectively, "Honeyshares Content"), are owned by or licensed to Honeyshares and are protected by copyright, trademark, and other laws.
              </p>
              <p className="text-neutral-700 mb-4">
                Except as expressly permitted in these Terms or as otherwise authorized in writing by us:
              </p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>You may not copy, reproduce, distribute, modify, create derivative works of, publicly display, publicly perform, republish, download, store, transmit, or exploit any Honeyshares Content;</li>
                <li>You may not use any data mining, robots, scraping, or similar data gathering or extraction methods;</li>
                <li>You may not remove, alter, or obscure any copyright, trademark, or other proprietary notices.</li>
              </ul>
            </section>

            {/* Section 8 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">8. User Content and Feedback</h2>
              <p className="text-neutral-700 mb-4">
                If you submit, upload, or otherwise provide any content to or through the Services, including feedback, comments, questions, ideas, suggestions, or other materials (collectively, "User Content"), you grant Honeyshares a non-exclusive, worldwide, royalty-free, perpetual, irrevocable, sublicensable, and transferable license to use, reproduce, modify, adapt, publish, translate, create derivative works from, distribute, perform, and display such User Content in any form and for any purpose related to operating, improving, and promoting the Services and our business.
              </p>
              <p className="text-neutral-700 mb-4">You represent and warrant that:</p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>You own or have the necessary rights to submit such User Content;</li>
                <li>Your User Content does not violate any law or these Terms;</li>
                <li>Your User Content does not infringe any third party's rights; and</li>
                <li>Your User Content is accurate and not misleading.</li>
              </ul>
              <p className="text-neutral-700 mb-4">
                We are not obligated to use or publish any User Content and may remove or edit any User Content at any time in our sole discretion.
              </p>
              <p className="text-neutral-700 mb-4">
                If you send Honeyshares any feedback or suggestions regarding the Services, you acknowledge that:
              </p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>Such feedback is non-confidential and non-proprietary;</li>
                <li>We may use, disclose, or incorporate such feedback without any obligation to you;</li>
                <li>You waive any rights in such feedback and assign all rights therein to us;</li>
                <li>You will not seek compensation for any feedback you provide.</li>
              </ul>
            </section>

            {/* Section 9 */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">9. Third-Party Sites, Services, and Content</h2>
              <p className="text-neutral-700 mb-4">
                The Services may contain links to, or integrations with, third-party websites, platforms, tools, or services ("Third-Party Services"). These are provided for your convenience only.
              </p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>We do not control Third-Party Services and are not responsible for their content, privacy practices, or availability;</li>
                <li>Your use of Third-Party Services is at your own risk and subject to their own terms and policies;</li>
                <li>Inclusion of a link or integration does not constitute endorsement;</li>
                <li>We may use third-party service providers to facilitate parts of the Services (e.g., identity verification, payment processing, or analytics).</li>
              </ul>
            </section>

            {/* Section 10 - Disclaimers */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">10. Disclaimers</h2>
              <div className="bg-neutral-100 border border-neutral-200 rounded-lg p-4 mb-4">
                <p className="text-neutral-700 font-medium">
                  THE SERVICES, HONEYSHARES CONTENT, AND ALL MATERIALS, INFORMATION, SOFTWARE, PRODUCTS, AND SERVICES INCLUDED IN OR AVAILABLE THROUGH THE SERVICES ARE PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED.
                </p>
              </div>
              <p className="text-neutral-700 mb-4">
                TO THE FULLEST EXTENT PERMITTED BY LAW, HONEYSHARES DISCLAIMS ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, NON-INFRINGEMENT, ACCURACY, COMPLETENESS, RELIABILITY, SECURITY, OR AVAILABILITY.
              </p>
              <p className="text-neutral-700">
                We do not warrant that the Services will be uninterrupted, error-free, secure, or free of viruses or other harmful components.
              </p>
            </section>

            {/* Section 11 - Limitation of Liability */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">11. Limitation of Liability</h2>
              <div className="bg-neutral-100 border border-neutral-200 rounded-lg p-4 mb-4">
                <p className="text-neutral-700 font-medium">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW, HONEYSHARES AND ITS AFFILIATES, OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, PARTNERS, AND LICENSORS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES.
                </p>
              </div>
              <p className="text-neutral-700">
                IN NO EVENT SHALL OUR TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICES EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID TO US, IF ANY, IN THE TWELVE (12) MONTHS PRIOR TO THE CLAIM, OR (B) ONE HUNDRED DOLLARS ($100).
              </p>
            </section>

            {/* Section 12 - Indemnification */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">12. Indemnification</h2>
              <p className="text-neutral-700">
                You agree to indemnify, defend, and hold harmless Honeyshares and its affiliates, officers, directors, employees, agents, partners, and licensors from and against any and all claims, liabilities, damages, losses, costs, and expenses (including reasonable attorneys' fees) arising out of or relating to your use of the Services, your User Content, your violation of these Terms, or your violation of any rights of another.
              </p>
            </section>

            {/* Section 13 - Arbitration and Class Action Waiver */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-foreground mb-4">13. Dispute Resolution; Binding Arbitration; Class Action Waiver</h2>
              <div className="bg-[#F0F0F0] border-2 border-[#242424] rounded-lg p-4 mb-4">
                <p className="text-[#242424] font-medium">
                  PLEASE READ THIS SECTION CAREFULLY. IT AFFECTS YOUR LEGAL RIGHTS, INCLUDING YOUR RIGHT TO FILE A LAWSUIT IN COURT.
                </p>
              </div>
              <p className="text-neutral-700 mb-4">
                You and Honeyshares agree that any dispute, claim, or controversy arising out of or relating to these Terms or the Services will be resolved through binding arbitration, rather than in court, except that you may assert claims in small claims court if your claims qualify.
              </p>
              <p className="text-neutral-700 mb-4">
                <strong>Class Action Waiver:</strong> YOU AND HONEYSHARES AGREE THAT ANY CLAIMS WILL BE BROUGHT ONLY IN YOUR OR ITS INDIVIDUAL CAPACITY, AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS OR REPRESENTATIVE PROCEEDING.
              </p>
              <p className="text-neutral-700">
                The arbitration will be administered by JAMS pursuant to its Comprehensive Arbitration Rules and Procedures, and the arbitrator's decision will be final and binding.
              </p>
            </section>

            {/* Section 14 - Governing Law */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">14. Governing Law</h2>
              <p className="text-neutral-700">
                These Terms and your use of the Services will be governed by and construed in accordance with the laws of the State of Delaware, without regard to its conflict of law principles. Any legal action or proceeding not subject to arbitration shall be brought exclusively in the state or federal courts located in Delaware.
              </p>
            </section>

            {/* Section 15 - Termination */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">15. Termination</h2>
              <p className="text-neutral-700 mb-4">
                We may suspend or terminate your access to the Services at any time, with or without cause, and with or without notice. Upon termination:
              </p>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li>All licenses granted to you under these Terms will immediately terminate;</li>
                <li>You must cease all use of the Services;</li>
                <li>Sections of these Terms that by their nature should survive termination will survive.</li>
              </ul>
            </section>

            {/* Section 16 - General Provisions */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">16. General Provisions</h2>
              <ul className="list-disc pl-6 mb-4 text-neutral-700 space-y-2">
                <li><strong>Entire Agreement:</strong> These Terms, together with the Privacy Policy and any other terms incorporated by reference, constitute the entire agreement between you and Honeyshares regarding the Services.</li>
                <li><strong>Waiver:</strong> Our failure to enforce any provision of these Terms shall not constitute a waiver of that provision or any other provision.</li>
                <li><strong>Severability:</strong> If any provision of these Terms is found to be invalid or unenforceable, the remaining provisions shall remain in full force and effect.</li>
                <li><strong>Assignment:</strong> You may not assign or transfer these Terms without our prior written consent. We may assign these Terms without restriction.</li>
                <li><strong>No Agency:</strong> Nothing in these Terms creates any agency, partnership, joint venture, or employment relationship between you and Honeyshares.</li>
              </ul>
            </section>

            {/* Section 17 - Contact */}
            <section className="mb-8">
              <h2 className="text-2xl font-semibold text-neutral-900 mb-4">17. Contact Us</h2>
              <p className="text-neutral-700 mb-4">
                If you have questions about these Terms, please contact us at:
              </p>
              <div className="bg-neutral-100 rounded-lg p-4">
                <p className="text-neutral-700 font-medium">Honeyshares LLC</p>
                <p className="text-neutral-700">Email: <a href="mailto:support@honeyshares.com" className="text-[#1944FF] font-medium hover:underline">support@honeyshares.com</a></p>
              </div>
            </section>

            {/* Investment Disclaimer */}
            <section className="mb-8 mt-12 border-t border-border pt-8">
              <h2 className="text-xl font-semibold text-foreground mb-4">Important Investment Disclaimer</h2>
              <div className="bg-[#F0F0F0] border-2 border-[#242424] rounded-lg p-6">
                <p className="text-foreground text-sm leading-relaxed">
                  Honeyshares and its affiliates do not provide investment, legal, or tax advice. The information provided through the Services is for general informational and educational purposes only and is not tailored to any individual's personal circumstances. You should consult with qualified professionals before making any investment decision. All investments involve risk, including the possible loss of principal. Past performance is not indicative of future results. You should carefully review all offering materials and consult with your own legal, tax, and financial advisers before making any investment.
                </p>
              </div>
            </section>
          </div>

          {/* Footer Links */}
          <div className="mt-12 pt-8 border-t border-border">
            <div className="flex flex-wrap justify-center gap-6 text-sm">
              <Link to="/privacy" className="text-[#1944FF] font-medium hover:underline">Visit Privacy Policy</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
